import { useId, useState, type ReactElement } from "react";
import { FR_LABELS, type ConsentLabels } from "./labels";
import {
  OPTIONAL_CATEGORIES,
  rejectAll,
  type ConsentCategory,
  type ConsentDecision,
} from "./types";
import type { UseCookieConsent } from "./useCookieConsent";

export interface CookieBannerProps {
  /** The consent state hook result (from `useCookieConsent`). */
  consent: UseCookieConsent;
  /** Localised copy — defaults to French (ADR-0013). */
  labels?: ConsentLabels;
}

/**
 * The diner-facing cookie-consent banner (features/privacy-and-consent.md R1). Accept-all / reject-all
 * / customise across the four categories. Rendered as an accessible modal dialog (`role="dialog"`,
 * `aria-modal`, labelled + described) so it is keyboard-navigable and axe-clean (ADR-0023). It shows
 * ONLY while `consent.needsChoice` is true — once a choice is stored it disappears until re-prompt.
 *
 * Reject-all is a first-class button (never buried), so declining is exactly as easy as accepting
 * (R1, CNIL). No network request fires from here beyond the server receipt the hook records — the
 * analytics adapter and third-party embeds stay dormant until their category is granted.
 */
export function CookieBanner({
  consent,
  labels = FR_LABELS,
}: CookieBannerProps): ReactElement | null {
  const titleId = useId();
  const bodyId = useId();
  const [customising, setCustomising] = useState(false);
  const [draft, setDraft] = useState<ConsentDecision>(rejectAll);

  if (!consent.needsChoice) return null;

  const toggle = (category: ConsentCategory) =>
    setDraft((prev) => ({ ...prev, [category]: !prev[category] }));

  return (
    <dialog
      open
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-testid="cookie-banner"
    >
      <h2 id={titleId}>{labels.bannerTitle}</h2>
      <p id={bodyId}>{labels.bannerBody}</p>

      {customising && (
        <fieldset>
          <legend>{labels.customise}</legend>
          {OPTIONAL_CATEGORIES.map((category) => (
            <label key={category}>
              <input
                type="checkbox"
                checked={draft[category]}
                onChange={() => toggle(category)}
                data-testid={`toggle-${category}`}
              />
              <span>{labels.categories[category].name}</span>
              <span>{labels.categories[category].description}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div>
        <button type="button" onClick={() => consent.rejectAll()} data-testid="reject-all">
          {labels.rejectAll}
        </button>
        {customising ? (
          <button type="button" onClick={() => consent.save(draft)} data-testid="save-choices">
            {labels.save}
          </button>
        ) : (
          <button type="button" onClick={() => setCustomising(true)} data-testid="customise">
            {labels.customise}
          </button>
        )}
        <button type="button" onClick={() => consent.acceptAll()} data-testid="accept-all">
          {labels.acceptAll}
        </button>
      </div>
    </dialog>
  );
}
